import type { ZustandStore } from '../store.type';
import type { NamedSet } from 'zustand/middleware';

export type KeyValue = {
	key: string;
	value: string | string[] | boolean | number;
};

type Features = {
	ENABLE_DOMAIN_WHITELISTING?: boolean;
	ENABLE_FILTERED_BY_COSTCENTER?: boolean;
	ENABLE_OUT_OF_OFFICE_FORWARDING?: boolean;
	ENABLE_MULTIUSE?: boolean;
	ENABLE_SINGLEUSE?: boolean;
	ENABLE_SUBSCRIPTIONS?: boolean;
	ENABLE_TRANSACTION_RECEIPT_UPLOAD?: boolean;
	ENABLE_USER_APPROVAL_LIMITS?: boolean;
	ENABLE_VIRTUAL_CARD_EXP_RANGE?: boolean;
	PAUSE_VIRTUAL_CARD_CREATION?: boolean;
	ENABLE_API_INTEGRATION?: boolean;
	ENABLE_PROVIDER_MAPPING?: boolean;
	ENABLE_COMPANY_PEOPLE_VIEW?: boolean;
	ENABLE_GLOBALIZATION?: boolean;
};

type CompanyProperties = {
	defaultExpirationDate: string;
	whiteListedDomains: string[];
	companyApprovalLimit: string;
	'tsgo-requestlimit': number;
	REQUEST_LIMIT: number;
	showactivity: string;
};

export type CompanySyncState = {
	companyName: string;
	propertyValue?: any;
	properties: CompanyProperties;
	features?: Features;
};

export type CompanyState = {
	company: {
		companyName: string;
		propertyValue?: any;
		companyId: string;
		properties: CompanyProperties;
		features?: Features;
		uiSettings: {
			saveButton: {
				isDisabled: boolean;
			};
		};
		companyErrors: object;
		companySyncState: CompanySyncState;
	};
};

const defaultFeatures: Features = {
	ENABLE_DOMAIN_WHITELISTING: false,
	ENABLE_FILTERED_BY_COSTCENTER: false,
	ENABLE_OUT_OF_OFFICE_FORWARDING: false,
	ENABLE_MULTIUSE: false,
	ENABLE_SINGLEUSE: false,
	ENABLE_SUBSCRIPTIONS: false,
	ENABLE_TRANSACTION_RECEIPT_UPLOAD: false,
	ENABLE_USER_APPROVAL_LIMITS: false,
	ENABLE_VIRTUAL_CARD_EXP_RANGE: false,
	PAUSE_VIRTUAL_CARD_CREATION: false,
	ENABLE_API_INTEGRATION: false,
	ENABLE_PROVIDER_MAPPING: false,
	ENABLE_COMPANY_PEOPLE_VIEW: false,
	ENABLE_GLOBALIZATION: false,
};

const companyInitialState: CompanyState = {
	company: {
		companyName: '',
		companyId: '',
		features: defaultFeatures,
		properties: {
			whiteListedDomains: [] as string[],
			companyApprovalLimit: '',
			defaultExpirationDate: '',
		} as CompanyProperties,
		uiSettings: {
			saveButton: {
				isDisabled: true,
			},
		},
		companyErrors: {},
		companySyncState: {
			companyName: '',
			features: defaultFeatures,
			properties: {
				whiteListedDomains: [] as string[],
				companyApprovalLimit: '',
				defaultExpirationDate: '',
				showactivity: 'false',
			},
		} as CompanySyncState,
	},
};

export type CompanyActions = {
	setCompanyName: (companyName: CompanyState['company']['companyName']) => void;
	setCompanyId: (companyId: CompanyState['company']['companyId']) => void;
	setCompanyFeatures: (features: CompanyState['company']['features']) => void;
	setCompanyFeature: ({ key, value }: KeyValue) => void;
	setCompanyProperty: ({ key, value }: KeyValue) => void;
	setIsCompanySettingsSaveBtnDisabled: (
		isDisabled: CompanyState['company']['uiSettings']['saveButton']['isDisabled'],
	) => void;
	setCompanyErrors: (companyErrors: CompanyState['company']['companyErrors']) => void;
	setCompanySyncState: (companySyncState: CompanyState['company']['companySyncState']) => void;
};

export type CompanySlice = CompanyState & CompanyActions;

export function companySlice(set: NamedSet<ZustandStore>): CompanySlice {
	return {
		...companyInitialState,
		setCompanyName(companyName: CompanyState['company']['companyName']): void {
			set(
				(state: CompanyState) => {
					state.company.companyName = companyName;
					return state;
				},
				false,
				`[setCompanyName]-${companyName}`,
			);
		},
		setCompanyId(companyId: CompanyState['company']['companyId']): void {
			set(
				(state: CompanyState) => {
					state.company.companyId = companyId;
					return state;
				},
				false,
				'[setCompanyId]',
			);
		},
		setCompanyFeatures(features: CompanyState['company']['features']): void {
			set(
				(state: CompanyState) => {
					state.company.features = { ...state.company.features, ...features };
					return state;
				},
				false,
				'[setCompanyFeatures]',
			);
		},
		setCompanyFeature({ key, value }: KeyValue): void {
			set(
				(state: CompanyState) => {
					if (state.company.features) {
						state.company.features[key] = value;
					}
					return state;
				},
				false,
				'[setCompanyFeature]',
			);
		},
		setCompanyProperty({ key, value }: KeyValue): void {
			set(
				(state: CompanyState) => {
					state.company.properties[key] = key === 'whiteListedDomains' ? (String(value) as string).split(',') : value;
					return state;
				},
				false,
				'[setCompanyProperty]',
			);
		},
		setIsCompanySettingsSaveBtnDisabled(
			isDisabled: CompanyState['company']['uiSettings']['saveButton']['isDisabled'],
		): void {
			set(
				(state: CompanyState) => {
					state.company.uiSettings.saveButton.isDisabled = isDisabled;
					return state;
				},
				false,
				'[setIsCompanySettingsSaveBtnDisabled]',
			);
		},
		setCompanyErrors(companyErrors: CompanyState['company']['companyErrors']): void {
			set(
				(state: CompanyState) => {
					state.company.companyErrors = companyErrors;
					return state;
				},
				false,
				'[setCompanyErrors]',
			);
		},
		setCompanySyncState(companySyncState: CompanyState['company']['companySyncState']): void {
			set(
				(state: CompanyState) => {
					state.company.companySyncState = companySyncState;
					return state;
				},
				false,
				'[setCompanySyncState]',
			);
		},
	};
}
